import {Box, Group, Accordion, Alert, Progress, Text, createStyles} from '@mantine/core';
import React, {useMemo} from 'react';

import {TablePerformanceSummary} from 'notebook/performance/tableLimitUtils';
import {CostBreakdownKey} from 'notebook/tables/excess/CostAnalyzer';
import {GRAY_LIGHTER, BLUE_LIGHTER, GRAY_LIGHT} from 'ui-library/Colors';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import {Title} from 'ui-library/Headers';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import sortedBy from 'util/sortedBy';

const HIGHEST_IMPACT_MESSAGE =
  "This table is the biggest contributor to your entry's performance score — both its table cell count and formula complexity are high. Moving completed run data to a separate entry will have the most impact. Alternatively, replacing finalized formula table cells with static values (copy → paste as values) can significantly reduce the processing load.";
const CELLS_AND_FORMULAS_MESSAGE =
  'This table has a high performance score driven by both its size and complex formulas. Replacing formula table cells with their calculated values once results are confirmed will reduce the score without losing any data.';
const CELLS_ONLY_MESSAGE =
  "This table's score is driven mainly by its table cell count. Consider removing rows that are no longer needed, or moving historical data to a separate entry.";
const FORMULAS_ONLY_MESSAGE =
  "This table's score is driven mainly by its formula complexity. Consider replacing formula table cells with their calculated values once results are confirmed.";
const LOW_IMPACT_MESSAGE =
  'This table has a low performance score — its table cell count and formula complexity are not significantly impacting your entry.';
const PERFORMANCE_IMPACT_COLUMN_TOOLTIP =
  "Each table's contribution to the overall entry performance score, based on its table cell count and the complexity of the formulas it contains. Tables with a higher contribution have the greatest impact on entry performance.";
interface TablePerformanceRowProps {
  row: TablePerformanceSummary;
  totalFormulaCost: number;
  highestCostTable: number;
  showStepNameColumn: boolean;
}

function getPerformanceMessage(
  row: TablePerformanceSummary,
  totalFormulaCost: number,
  highestCostTable: number
): {title: string; message: string} {
  const pct = totalFormulaCost > 0 ? row.formulaCost / totalFormulaCost : 0;

  if (pct < 0.1) {
    return {title: '', message: LOW_IMPACT_MESSAGE};
  }

  const title = "How to reduce this table's impact";

  if (row.formulaCost === highestCostTable) {
    return {title, message: HIGHEST_IMPACT_MESSAGE};
  }

  const sortedCostBreakdown = Object.entries(row.costBreakdown).sort((a, b) => b[1] - a[1]);
  const topThree: {[key: string]: number} = {};
  for (const [key, value] of sortedCostBreakdown.slice(0, 3)) {
    topThree[key] = value;
  }

  if (topThree[CostBreakdownKey.CELL_COUNT]) {
    const topThreeTotalCount = Object.values(topThree).reduce((acc, value) => acc + value, 0);
    const cellCountPct = topThree[CostBreakdownKey.CELL_COUNT] / topThreeTotalCount;
    if (cellCountPct > 0.8) {
      return {title, message: CELLS_ONLY_MESSAGE};
    } else if (cellCountPct > 0.5) {
      return {title, message: CELLS_AND_FORMULAS_MESSAGE};
    }
  }

  return {title, message: FORMULAS_ONLY_MESSAGE};
}

function TablePerformanceRow({
  row,
  totalFormulaCost,
  highestCostTable,
  showStepNameColumn,
}: TablePerformanceRowProps): JSX.Element {
  const {classes} = useStyles();
  const {tableId, formulaCost} = row;
  const pct = totalFormulaCost > 0 ? formulaCost / totalFormulaCost : 0;
  const {title, message} = getPerformanceMessage(row, totalFormulaCost, highestCostTable);

  return (
    <Accordion.Item key={tableId} value={tableId}>
      <Accordion.Control>
        <Group>
          <Box className={classes.flexItem}>
            <div className={classes.nameText}>{row.name}</div>
          </Box>
          {showStepNameColumn && (
            <Box className={classes.flexItem}>
              <div className={classes.nameText}>{row.documentName}</div>
            </Box>
          )}
          <Box className={classes.flexItem}>
            <Group>
              <Box className={classes.flexItem}>
                <Progress value={pct * 100} color={BLUE_LIGHTER} size={16} radius="md" />
              </Box>
              <Text className={classes.percentageLabel}>{Math.round(pct * 100)}%</Text>
            </Group>
          </Box>
        </Group>
      </Accordion.Control>
      <Accordion.Panel>
        <Alert title={title}>
          <Text>{message}</Text>
          <Text>Cell Count: {row.numCells}</Text>
        </Alert>
      </Accordion.Panel>
    </Accordion.Item>
  );
}

interface TablePerformanceSummaryTableProps {
  tableSummaries: Array<TablePerformanceSummary>;
  totalFormulaCost: number;
  showStepNameColumn: boolean;
}

export function TablePerformanceSummaryTable({
  tableSummaries,
  totalFormulaCost,
  showStepNameColumn = false,
}: TablePerformanceSummaryTableProps): JSX.Element {
  const {classes} = useStyles();

  const sortedRows = sortedBy(tableSummaries, [
    {
      accessor: (summary: TablePerformanceSummary): number => summary.formulaCost,
      reversed: true,
    },
  ]);

  const highestCostTable = useMemo(
    () => sortedRows.reduce((max, row) => Math.max(max, row.formulaCost), 0),
    [sortedRows]
  );

  return (
    <>
      <Box className={classes.performanceImpactTable}>
        <Group className={classes.performanceImpactTableHeader}>
          <Box className={classes.flexItem}>
            <Title>Table Name</Title>
          </Box>
          {showStepNameColumn && (
            <Box className={classes.flexItem}>
              <Title>Step Name</Title>
            </Box>
          )}
          <Box className={classes.flexItem}>
            <Group align="center">
              <Title>Performance Impact</Title>
              <Tooltip text={PERFORMANCE_IMPACT_COLUMN_TOOLTIP}>
                <BenchlingIcon className={classes.performanceImpactTooltipIcon} icon="circle-question" />
              </Tooltip>
            </Group>
          </Box>
        </Group>
        <Accordion>
          {sortedRows.map((row) => (
            <TablePerformanceRow
              key={row.tableId}
              row={row}
              totalFormulaCost={totalFormulaCost}
              highestCostTable={highestCostTable}
              showStepNameColumn={showStepNameColumn}
            />
          ))}
        </Accordion>
      </Box>
    </>
  );
}

const useStyles = createStyles(() => ({
  nameText: {
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'pre-wrap',
  },
  percentageLabel: {
    width: '30px',
  },
  performanceImpactTable: {
    border: `1px solid ${GRAY_LIGHT}`,
  },
  performanceImpactTableHeader: {
    background: GRAY_LIGHTER,
    padding: '5px',
  },
  flexItem: {
    flex: 1,
  },
  performanceImpactTooltipIcon: {
    marginBottom: '2px',
  },
}));

export const _forTesting = {
  getPerformanceMessage,
  TablePerformanceRow,
};
